import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Form, Stack, ProgressBar } from "react-bootstrap";
import useApiClient from "../hooks/useApiClient";
import { CategorySelect, CountType } from "../components/inputs/categorySelect";
import { CategoryWithCountsViewModel, CreateVideoBindingModel, CreateVideosBindingModel, CreateBookmarkBindingModel, CreateBookmarksBindingModel } from "../apiClient";
import { getErrorMessage } from "../helpers/errorHelper";
import { notify, notificationDetailAutoCloseMs } from "../helpers/alertHelper";

export interface HistoryItem {
    id: number;
    url: string;
    status: 'pending' | 'success' | 'error';
    message?: string;
    type: 'video' | 'bookmark';
    category: string;
}

interface QuickAddContextType {
    history: HistoryItem[];
    clearHistory: () => void;
    registerRefreshHandler: (key: 'videos' | 'bookmarks', handler: () => void) => () => void;
}

const QuickAddContext = createContext<QuickAddContextType | undefined>(undefined);

export const useQuickAdd = () => {
    const context = useContext(QuickAddContext);
    if (!context) {
        throw new Error("useQuickAdd must be used within a QuickAddProvider");
    }
    return context;
};

const createErrorSummaryElement = (results: { url: string; error?: string }[]) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'text-start overflow-auto p-2 mt-2 border rounded bg-body-tertiary text-body';
    wrapper.style.maxHeight = '250px';

    results.forEach(r => {
        const itemEl = document.createElement('div');
        itemEl.className = 'mb-2 pb-1 border-bottom';

        const urlEl = document.createElement('div');
        urlEl.className = 'fw-bold text-break';
        urlEl.textContent = r.url;

        const reasonEl = document.createElement('div');
        reasonEl.className = 'text-danger small mt-1';
        reasonEl.textContent = r.error || 'Unknown error';

        itemEl.appendChild(urlEl);
        itemEl.appendChild(reasonEl);
        wrapper.appendChild(itemEl);
    });

    return wrapper;
};

const processQuickAddItem = async (
    apiClient: any,
    item: { id: number; url: string; type: 'video' | 'bookmark' },
    category: string
): Promise<{ success: boolean; finalType?: 'video' | 'bookmark'; error?: string }> => {
    const categoryName = category || null;

    if (item.type === 'video') {
        const videoResult = await apiClient?.videosPOST(new CreateVideosBindingModel({
            videos: [new CreateVideoBindingModel({ url: item.url, categoryName })]
        }));

        const failedVideo = videoResult?.failedVideos?.[0];
        if (!failedVideo) {
            return { success: true, finalType: 'video' };
        }

        // Fallback to bookmark add if video add fails
        const bookmarkResult = await apiClient?.bookmarksPOST(new CreateBookmarksBindingModel({
            bookmarks: [new CreateBookmarkBindingModel({ url: item.url, categoryName })]
        }));

        const failedBookmark = bookmarkResult?.failedBookmarks?.[0];
        if (!failedBookmark) {
            return { success: true, finalType: 'bookmark' };
        }

        const combinedReason = `Video: ${failedVideo.reason} | Bookmark: ${failedBookmark.reason}`;
        return { success: false, error: combinedReason };
    } else {
        const bookmarkResult = await apiClient?.bookmarksPOST(new CreateBookmarksBindingModel({
            bookmarks: [new CreateBookmarkBindingModel({ url: item.url, categoryName })]
        }));

        const failedBookmark = bookmarkResult?.failedBookmarks?.[0];
        if (!failedBookmark) {
            return { success: true, finalType: 'bookmark' };
        }

        const reason = failedBookmark.reason ?? 'Failed to add bookmark.';
        return { success: false, error: reason };
    }
};

export const QuickAddProvider = ({ children }: { children: React.ReactNode }) => {
    const apiClient = useApiClient();
    const [categories, setCategories] = useState<CategoryWithCountsViewModel[]>([]);
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const nextIdRef = useRef(1);
    const refreshHandlersRef = useRef<{ [key in 'videos' | 'bookmarks']?: () => void }>({});

    const registerRefreshHandler = useCallback((key: 'videos' | 'bookmarks', handler: () => void) => {
        refreshHandlersRef.current[key] = handler;
        return () => {
            if (refreshHandlersRef.current[key] === handler) {
                delete refreshHandlersRef.current[key];
            }
        };
    }, []);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [pendingUrls, setPendingUrls] = useState<string[]>([]);
    const [categoryName, setCategoryName] = useState<string>('');
    const [addType, setAddType] = useState<'video' | 'bookmark'>('video');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progressCurrent, setProgressCurrent] = useState<number>(0);
    const [progressTotal, setProgressTotal] = useState<number>(0);
    const [currentAddingUrl, setCurrentAddingUrl] = useState<string>('');

    const refreshCategories = useCallback(async () => {
        if (!apiClient) return;
        try {
            const cats = await apiClient.categoriesAll();
            setCategories(cats);
        } catch (e) {
            console.error("Failed to load categories", e);
        }
    }, [apiClient]);

    useEffect(() => {
        if (apiClient) {
            refreshCategories();
        }
    }, [apiClient, refreshCategories]);

    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (showModal) return;

            const active = document.activeElement;
            if (active) {
                const tagName = active.tagName.toLowerCase();
                const isInput = tagName === 'input' || tagName === 'textarea' || active.hasAttribute('contenteditable');
                if (isInput) return;
            }

            const pastedText = e.clipboardData?.getData('text');
            if (!pastedText) return;

            const urls = pastedText.split('\n')
                .map(u => u.trim())
                .filter(u => u.startsWith('http://') || u.startsWith('https://'));

            if (urls.length === 0) return;

            e.preventDefault();
            refreshCategories();

            setPendingUrls(urls);
            setAddType('video');
            setShowModal(true);
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [showModal, refreshCategories]);

    const handleClose = () => {
        setShowModal(false);
        setPendingUrls([]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsSubmitting(true);

        const urls = pendingUrls;
        const currentCategory = categoryName;
        const currentAddType = addType;

        const newItems: HistoryItem[] = urls.map((url, i) => ({
            id: nextIdRef.current + i,
            url,
            status: 'pending',
            type: currentAddType,
            category: currentCategory || 'None'
        }));

        nextIdRef.current += urls.length;
        setHistory(prev => [...newItems, ...prev]);

        setProgressTotal(urls.length);
        setProgressCurrent(0);

        let successCount = 0;
        let errorCount = 0;
        const results: { url: string; error?: string }[] = [];

        for (const item of newItems) {
            setCurrentAddingUrl(item.url);
            try {
                const res = await processQuickAddItem(apiClient, item, currentCategory);
                if (res.success) {
                    successCount++;
                    setHistory(prev => prev.map(h => h.id === item.id ? { ...h, status: 'success', type: res.finalType ?? h.type } : h));
                } else {
                    errorCount++;
                    const reason = res.error || 'Failed to add item.';
                    results.push({ url: item.url, error: reason });
                    setHistory(prev => prev.map(h => h.id === item.id ? { ...h, status: 'error', message: reason } : h));
                }
            } catch (err) {
                errorCount++;
                const reason = getErrorMessage(err);
                results.push({ url: item.url, error: reason });
                setHistory(prev => prev.map(h => h.id === item.id ? { ...h, status: 'error', message: reason } : h));
            }
            setProgressCurrent(prev => prev + 1);
        }

        if (successCount > 0) {
            const path = window.location.pathname;
            if (path === "/" && refreshHandlersRef.current['videos']) {
                refreshHandlersRef.current['videos']();
            } else if (path === "/bookmarks" && refreshHandlersRef.current['bookmarks']) {
                refreshHandlersRef.current['bookmarks']();
            }
        }

        setShowModal(false);
        setPendingUrls([]);
        setIsSubmitting(false);
        setProgressCurrent(0);
        setProgressTotal(0);
        setCurrentAddingUrl('');

        if (window.location.pathname !== "/quick-add") {
            if (errorCount === 0) {
                notify({
                    title: "Added!",
                    text: `${successCount} ${successCount === 1 ? 'item' : 'items'} added successfully.`,
                    icon: "success",
                });
            } else {
                const wrapper = createErrorSummaryElement(results);
                const title = successCount === 0
                    ? "Failed to add items"
                    : `Completed with errors (${successCount} added, ${errorCount} failed)`;
                const icon = successCount === 0 ? "error" : "warning";

                notify({ title, content: wrapper, icon }, notificationDetailAutoCloseMs);
            }
        }
    };

    const handleRadioKeyDown = (e: React.KeyboardEvent, targetType: 'video' | 'bookmark') => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const nextType = targetType === 'video' ? 'bookmark' : 'video';
            setAddType(nextType);
            document.getElementById(`modal-type-${nextType}`)?.focus();
        }
    };

    const clearHistory = useCallback(() => {
        setHistory([]);
    }, []);

    return (
        <QuickAddContext.Provider value={{ history, clearHistory, registerRefreshHandler }}>
            {children}
            <Modal
                show={showModal}
                onHide={isSubmitting ? undefined : handleClose}
                backdrop={isSubmitting ? "static" : true}
                keyboard={!isSubmitting}
                animation={false}
                size="lg"
            >
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton={!isSubmitting}>
                        <Modal.Title>Add {pendingUrls.length} {pendingUrls.length === 1 ? 'Link' : 'Links'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Stack gap={3}>
                            {isSubmitting ? (
                                <div className="py-3">
                                    <h5 className="mb-3">Adding items...</h5>
                                    <div className="mb-2 text-muted text-truncate" style={{ maxWidth: '100%' }}>
                                        Processing: {currentAddingUrl}
                                    </div>
                                    <ProgressBar
                                        animated
                                        now={progressTotal > 0 ? (progressCurrent / progressTotal) * 100 : 0}
                                        label={`${progressCurrent}/${progressTotal}`}
                                        className="mb-2"
                                    />
                                    <div className="small text-secondary">
                                        Completed {progressCurrent} of {progressTotal}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <p className="text-muted mb-0 text-truncate">{pendingUrls.join(', ')}</p>

                                    <Form.Group>
                                        <Form.Label>Item Type</Form.Label>
                                        <div>
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Video"
                                                name="modalAddType"
                                                id="modal-type-video"
                                                checked={addType === 'video'}
                                                onChange={() => setAddType('video')}
                                                onKeyDown={(e: any) => handleRadioKeyDown(e, 'video')}
                                                disabled={isSubmitting}
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Bookmark"
                                                name="modalAddType"
                                                id="modal-type-bookmark"
                                                checked={addType === 'bookmark'}
                                                onChange={() => setAddType('bookmark')}
                                                onKeyDown={(e: any) => handleRadioKeyDown(e, 'bookmark')}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group>
                                        <CategorySelect
                                            categories={categories}
                                            name="modalCategoryName"
                                            value={categoryName}
                                            onChange={val => setCategoryName(val)}
                                            countType={addType === 'video' ? CountType.Videos : CountType.Bookmarks}
                                            autoFocus={true}
                                            disabled={isSubmitting}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </Stack>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            Add Items
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </QuickAddContext.Provider>
    );
};
